import { HttpClient } from "@angular/common/http";
import { Injectable, inject } from "@angular/core";
import { buildOauthScope, type PublicLoginConfig } from "@partikelart/identity/domain";
import { AuthConfig } from "angular-oauth2-oidc";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";

/**
 * Home-realm discovery (D3 frontend).
 *
 * Before login, the SPA asks the backend which IdP to use for a given email
 * domain. The response (`PublicLoginConfig`) is translated into an
 * `angular-oauth2-oidc` `AuthConfig` and fed to `OAuthService.configure()`.
 *
 * This replaces the compile-time `config.<customer>.ts` files — the IdP is
 * now a database row selected at runtime, so a single build serves every
 * customer.
 */
@Injectable({ providedIn: "root" })
export class LoginConfigService {
	private readonly http = inject(HttpClient);

	/**
	 * Fetch the login config for `email` (or the deployment default when email
	 * is omitted) and translate it into an `AuthConfig` for angular-oauth2-oidc.
	 */
	loadAuthConfig(portalBackendUrl: string, email?: string): Observable<AuthConfig> {
		const params = email ? { email } : undefined;
		return this.http
			.get<PublicLoginConfig>(`${portalBackendUrl}/api/auth/login-config`, { params })
			.pipe(map((cfg) => this.toAuthConfig(cfg)));
	}

	/**
	 * Map the backend's public login config to the shape angular-oauth2-oidc
	 * expects. Front-channel (authorize) always goes to the real IdP; for
	 * `token-proxy` IdPs the backend has already rewritten the back-channel
	 * endpoints to `/api/auth/token` and `/api/auth/userinfo`.
	 */
	private toAuthConfig(cfg: PublicLoginConfig): AuthConfig {
		const scope = buildOauthScope("openid profile email", cfg);
		return {
			issuer: cfg.issuer,
			redirectUri: window.location.origin + "/redirect",
			postLogoutRedirectUri: window.location.origin + "/redirect",
			clientId: cfg.clientId ?? "",
			responseType: "code",
			scope,
			// The backend returns absolute endpoints (already rewritten for token-proxy).
			tokenEndpoint: cfg.tokenEndpoint ?? undefined,
			userinfoEndpoint: cfg.userinfoEndpoint ?? undefined,
			// Discover the authorize + JWKS endpoints from the issuer.
			strictDiscoveryDocumentValidation: false,
			showDebugInformation: false,
			clearHashAfterLogin: true,
			// Dedicated document (public/silent-refresh.html). The library default is
			// redirectUri, where the SPA fallback boots a second app in the iframe.
			silentRefreshRedirectUri: `${window.location.origin}/silent-refresh.html`,
			// prompt=none answers or fails fast, and the login sequence awaits this,
			// so tighten the 20 s library default.
			silentRefreshTimeout: 8000,
		};
	}
}
